(() => {
  "use strict";
  const $ = (selector, scope = document) => scope.querySelector(selector);
  const $$ = (selector, scope = document) => [
    ...scope.querySelectorAll(selector),
  ];
  const config = JSON.parse($("[data-theme-config]")?.textContent || "{}");
  const strings = config.strings || {};
  const root = (config.root || "/").replace(/\/?$/, "/");
  const money = (cents) =>
    new Intl.NumberFormat(config.locale || "fr-FR", {
      style: "currency",
      currency: config.currency || "EUR",
    }).format(cents / 100);
  const slug = (value) =>
    value
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-");
  let activeOverlay = null;
  let returnFocus = null;
  let inertElements = [];
  let cartBusy = false;

  // Inert siblings at each ancestor level, including Shopify section wrappers.
  function isolateOverlay(overlay) {
    let current = overlay;
    while (current && current !== document.body) {
      for (const sibling of current.parentElement.children) {
        if (
          sibling !== current &&
          !sibling.inert &&
          !sibling.matches("script, style, [data-drawer-backdrop]")
        ) {
          sibling.inert = true;
          inertElements.push(sibling);
        }
      }
      current = current.parentElement;
    }
  }
  function closeOverlay(restoreFocus = true) {
    if (!activeOverlay) return;
    activeOverlay.classList.remove("is-open");
    activeOverlay.setAttribute("aria-hidden", "true");
    activeOverlay.inert = true;
    $$(`[aria-controls="${activeOverlay.id}"]`).forEach((button) =>
      button.setAttribute("aria-expanded", "false"),
    );
    $("[data-drawer-backdrop]")?.classList.remove("is-open");
    inertElements.forEach((element) => {
      element.inert = false;
    });
    inertElements = [];
    document.body.classList.remove("no-scroll");
    activeOverlay = null;
    if (restoreFocus && returnFocus?.isConnected) returnFocus.focus();
  }
  function openOverlay(overlay, trigger = document.activeElement) {
    if (!overlay) return;
    const previousTrigger = returnFocus;
    const fromOverlay = activeOverlay?.contains(trigger);
    closeOverlay(false);
    returnFocus = fromOverlay ? previousTrigger : trigger;
    activeOverlay = overlay;
    overlay.inert = false;
    overlay.setAttribute("aria-hidden", "false");
    overlay.classList.add("is-open");
    $$(`[aria-controls="${overlay.id}"]`).forEach((button) =>
      button.setAttribute("aria-expanded", "true"),
    );
    if (overlay.matches("[data-cart-drawer]"))
      $("[data-drawer-backdrop]")?.classList.add("is-open");
    document.body.classList.add("no-scroll");
    isolateOverlay(overlay);
    (
      $('input[type="search"]', overlay) ||
      $(".ts-modal-close", overlay) ||
      $("button:not([tabindex='-1']), a[href]", overlay) ||
      overlay
    ).focus();
  }
  document.addEventListener("keydown", (event) => {
    if (!activeOverlay) return;
    if (event.key === "Escape") {
      event.preventDefault();
      closeOverlay();
    }
    if (event.key !== "Tab") return;
    const focusable = $$(
      'a[href], button:not(:disabled), input:not(:disabled), [tabindex="0"]',
      activeOverlay,
    ).filter(
      (element) =>
        element.tabIndex >= 0 &&
        element.getClientRects().length &&
        !element.closest("[hidden], [inert]"),
    );
    const first = focusable[0],
      last = focusable.at(-1);
    if (!first) {
      event.preventDefault();
      activeOverlay.focus();
    } else if (
      event.shiftKey &&
      (document.activeElement === first ||
        document.activeElement === activeOverlay)
    ) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  async function request(url, options = {}) {
    const response = await fetch(url, {
      cache: "no-store",
      ...options,
      headers: { Accept: "application/json", ...options.headers },
    });
    const data = await response.json();
    if (!response.ok)
      throw new Error(data.description || data.message || strings.error);
    return data;
  }
  const sectionIds = () => [
    ...new Set(
      $$("[data-cart-section]").map((section) => section.dataset.cartSection),
    ),
  ];
  function setCartBusy(busy) {
    cartBusy = busy;
    $$("[data-cart-change], [data-cart-upgrade]").forEach((button) => {
      button.setAttribute("aria-disabled", String(busy));
      if (button.tagName === "BUTTON") button.disabled = busy;
    });
    $$("[data-cart-section]").forEach((section) =>
      section.setAttribute("aria-busy", String(busy)),
    );
  }
  function cartError(message) {
    $$("[data-cart-error]").forEach((element) => {
      element.textContent = message;
      element.hidden = false;
    });
  }
  // Render Shopify's line items, discounts and totals, rather than reimplement pricing.
  async function refreshCart(sections) {
    const cart = await request(`${root}cart.js`);

    const ids = sectionIds();
    if (!sections || ids.some((id) => !sections[id])) {
      const url = new URL(`${root}cart`, location.origin);
      url.searchParams.set("sections", ids.join(","));
      sections = await request(url);
    }
    const replacements = ids.map((id) => {
      if (!sections[id]) throw new Error(strings.refreshError);
      const dom = new DOMParser().parseFromString(sections[id], "text/html");
      const fresh = $("[data-cart-content]", dom);
      const oldSection = $$("[data-cart-section]").find(
        (section) => section.dataset.cartSection === id,
      );
      const current = $("[data-cart-content]", oldSection);
      if (!fresh || !current) throw new Error(strings.refreshError);
      return { current, fresh };
    });
    const focused = document.activeElement;
    const focusKey = focused?.dataset?.key;
    const focusAction = focused?.dataset?.action;
    const focusContainer = focused?.closest("[data-cart-content]")?.dataset
      .cartContent;
    replacements.forEach(({ current, fresh }) => {
      current.innerHTML = fresh.innerHTML;
    });
    $$("[data-cart-count]").forEach((count) => {
      count.textContent = cart.item_count;
      count.hidden = cart.item_count === 0;
    });
    if (focusContainer) {
      const container = $$("[data-cart-content]").find(
        (node) => node.dataset.cartContent === focusContainer,
      );
      const next = $$("[data-cart-change]", container).find(
        (node) =>
          node.dataset.key === focusKey && node.dataset.action === focusAction,
      );
      (
        next ||
        $("button, a[href]", container) ||
        activeOverlay ||
        container
      ).focus();
    }
    $("[data-cart-status]").textContent = strings.updated;
    setCartBusy(cartBusy);
    return cart;
  }
  async function changeCart(button) {
    if (cartBusy || !button.isConnected) return;
    setCartBusy(true);
    let changed = false;
    try {
      // Resolve the current server line, especially when changing a selling plan.
      const latest = await request(`${root}cart.js`);
      const lineIndex = latest.items.findIndex(
        (item) => item.key === button.dataset.key,
      );
      if (lineIndex < 0) {
        await refreshCart();
        return;
      }
      const line = latest.items[lineIndex];
      const change = button.matches("[data-cart-upgrade]")
        ? {
            line: lineIndex + 1,
            quantity: line.quantity,
            selling_plan: Number(button.dataset.sellingPlan),
          }
        : {
            id: line.key,
            quantity:
              button.dataset.action === "plus"
                ? line.quantity + 1
                : button.dataset.action === "minus"
                  ? Math.max(0, line.quantity - 1)
                  : 0,
          };
      const result = await request(`${root}cart/change.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...change,
          sections: sectionIds(),
          sections_url: `${root}cart`,
        }),
      });
      changed = true;
      await refreshCart(result.sections);
    } catch (error) {
      // Read-only recovery never repeats a mutation that may have succeeded.
      try {
        await refreshCart();
      } catch (_) {
        /* Keep the explicit recovery message. */
      }
      cartError(
        changed ? strings.refreshError : error.message || strings.error,
      );
    } finally {
      setCartBusy(false);
    }
  }
  document.addEventListener("click", async (event) => {
    const target = event.target.closest("button, a, [data-drawer-backdrop]");
    if (!target) return;
    if (
      target.matches(
        ".js-cart-close, .js-menu-close, .js-search-close, [data-drawer-backdrop], [data-overlay-close]",
      )
    )
      closeOverlay();
    const overlaySelector = target.matches(".js-cart-open")
      ? "[data-cart-drawer]"
      : target.matches(".js-menu-open")
        ? "[data-mobile-menu]"
        : target.matches(".js-search-open")
          ? "[data-search-overlay]"
          : null;
    if (overlaySelector) {
      event.preventDefault();
      openOverlay($(overlaySelector), target);
      if (overlaySelector === "[data-cart-drawer]" && !cartBusy) {
        setCartBusy(true);
        try {
          await refreshCart();
        } catch (_) {
          cartError(strings.refreshError);
        } finally {
          setCartBusy(false);
        }
      }
    }
    if (target.matches("[data-cart-change], [data-cart-upgrade]")) {
      event.preventDefault();
      await changeCart(target);
    }
  });

  function initProduct(product) {
    if (product.dataset.initialized) return;
    product.dataset.initialized = "true";
    const data = JSON.parse($("[data-product-data]", product).textContent);
    const variants = data.variants;
    const form = $("[data-product-form]", product);
    const variantId = $("[data-variant-id]", form);
    const sellingPlan = $("[data-selling-plan]", form);
    const addButton = $("[data-add-button]", form);
    const errorBox = $("[data-product-error]", form);
    const selector = $("[data-subscription-selector]", product);
    const groups = $$(".product-option", product);
    const recharge = product.dataset.recharge === "true";
    const current = groups.map(
      (group) => $(".is-selected", group)?.dataset.optionValue,
    );
    let purchase =
      $('input[name="purchase_option"]:checked', product)?.value || "one-time";
    const initialParams = new URLSearchParams(location.search);
    const initialPlan = initialParams.get("selling_plan");
    if (initialParams.get("purchase") === "one-time") purchase = "one-time";
    if (
      initialPlan &&
      variants.some(
        (v) =>
          String(v.id) === variantId.value && String(v.plan_id) === initialPlan,
      )
    )
      purchase = "subscription";
    let currentVariant = null;
    let lastColor = null;
    let lastNativeImage = null;
    let submitting = false;
    const thumbnails = $$(".gallery-thumb", product);
    function selectImage(index) {
      const thumbnail = thumbnails[index];
      if (!thumbnail) return;
      const main = $(".gallery-main img", product);
      main.removeAttribute("srcset");
      main.src = thumbnail.dataset.gallerySrc;
      $("[data-gallery-reset]", product).hidden = false;
      main.alt = `${thumbnail.getAttribute("aria-label")} — ${current[Number(product.dataset.colorIndex)]}`;
      thumbnails.forEach((item, i) => {
        item.classList.toggle("is-active", i === index);
        item.setAttribute("aria-pressed", String(i === index));
      });
    }
    function resetGallery(color = lastColor) {
      const main = $(".gallery-main img", product);
      const source = data.gallery[color]?.[0];
      if (!main || !source) return;
      main.removeAttribute("srcset");
      main.src = source;
      main.alt = `${$("h1", product).textContent.trim()} — ${current[Number(product.dataset.colorIndex)]}`;
      thumbnails.forEach((item) => {
        item.classList.remove("is-active");
        item.setAttribute("aria-pressed", "false");
      });
      const reset = $("[data-gallery-reset]", product);
      if (reset) reset.hidden = true;
    }
    $("[data-gallery-reset]", product)?.addEventListener("click", () =>
      resetGallery(),
    );
    thumbnails.forEach((thumbnail, index) =>
      thumbnail.addEventListener("click", () => selectImage(index)),
    );
    function updateVariant(updateUrl = true) {
      currentVariant = variants.find((variant) =>
        variant.options.every((option, index) => current[index] === option),
      );
      const variant = currentVariant;
      const sub = recharge && purchase === "subscription";
      variantId.value = variant?.id || "";
      $("[data-selected-variant]", product).textContent =
        variant?.options.join(" · ") || strings.unavailable;
      if (sellingPlan) sellingPlan.value = sub ? variant?.plan_id || "" : "";
      const invalidPlan =
        (sub && !variant?.plan_id) || (variant?.requires_selling_plan && !sub);
      addButton.disabled = submitting || !variant?.available || invalidPlan;
      addButton.textContent = !variant
        ? strings.unavailable
        : !variant.available
          ? strings.soldOut
          : invalidPlan
            ? strings.subscriptionUnavailable
            : variant.preorder
              ? strings.preorder
              : strings.add;
      const preorderStatus = $("[data-preorder-status]", product);
      if (preorderStatus) preorderStatus.hidden = !variant?.preorder;
      const preorderProperty = $("[data-preorder-property]", product);
      if (preorderProperty) preorderProperty.disabled = !variant?.preorder;
      const preorderSetup = $("[data-preorder-setup]", product);
      if (preorderSetup)
        preorderSetup.hidden = !variant?.preorder || variant.available;
      const price = $("[data-current-price]", product);
      price.textContent = variant
        ? money(sub && variant.plan_id ? variant.plan_price : variant.price)
        : "—";
      const compare = $("[data-compare-price]", product);
      compare.hidden =
        !variant || sub || !(variant.compare_at_price > variant.price);
      compare.textContent = variant?.compare_at_price
        ? money(variant.compare_at_price)
        : "";
      if (selector) {
        $("[data-one-time-price]", selector).textContent = variant
          ? money(variant.price)
          : "—";
        $("[data-subscription-price]", selector).textContent = variant?.plan_id
          ? money(variant.plan_price)
          : "—";
        $('[value="subscription"]', selector).disabled = !variant?.plan_id;
        $('[value="one-time"]', selector).disabled = Boolean(
          variant?.requires_selling_plan,
        );
        $$("[data-purchase-option]", selector).forEach((option) => {
          const selected = option.dataset.purchaseOption === purchase;
          option.classList.toggle("is-selected", selected);
          $("input", option).checked = selected;
        });
        $("[data-purchase-note]", selector).hidden = !sub || !variant?.plan_id;
        $("[data-purchase-terms]", selector).hidden = !sub || !variant?.plan_id;
        $("[data-plan-unavailable]", selector).hidden = Boolean(
          variant?.plan_id,
        );
        $("[data-price-period]", product).hidden = !sub || !variant?.plan_id;
      }
      if (!recharge) {
        const color = slug(
          current[Number(product.dataset.colorIndex)] || "noir",
        )
          .replace(/^cherry$/, "cerise")
          .replace(/^(bleu|blue)$/, "tiffany");
        if (color !== lastColor && data.gallery[color]) {
          thumbnails.forEach((thumbnail) => {
            thumbnail.dataset.gallerySrc =
              data.gallery[color][Number(thumbnail.dataset.galleryIndex)];
            const image = $("img", thumbnail);
            image.removeAttribute("srcset");
            image.src = thumbnail.dataset.gallerySrc;
          });
          resetGallery(color);
          lastColor = color;
        }
      }
      if (recharge && variant?.image && variant.image !== lastNativeImage) {
        const mainImage = $(".gallery-main img", product);
        mainImage.removeAttribute("srcset");
        mainImage.src = variant.image;
        lastNativeImage = variant.image;
      }
      if (updateUrl) {
        const url = new URL(location.href);
        if (variant) url.searchParams.set("variant", variant.id);
        else url.searchParams.delete("variant");
        if (sub && variant?.plan_id)
          url.searchParams.set("selling_plan", variant.plan_id);
        else url.searchParams.delete("selling_plan");
        if (recharge) url.searchParams.set("purchase", purchase);
        history.replaceState({}, "", url);
      }
    }
    groups.forEach((group, index) => {
      $$(".option-chip", group).forEach((button) =>
        button.addEventListener("click", () => {
          current[index] = button.dataset.optionValue;
          $$(".option-chip", group).forEach((chip) => {
            const selected = chip === button;
            chip.classList.toggle("is-selected", selected);
            chip.setAttribute("aria-pressed", String(selected));
          });
          errorBox.hidden = true;
          updateVariant();
        }),
      );
    });
    selector?.addEventListener("change", (event) => {
      if (!event.target.matches('input[name="purchase_option"]')) return;
      purchase = event.target.value;
      updateVariant();
    });
    form.addEventListener("submit", async (event) => {
      event.preventDefault();
      if (submitting || cartBusy || addButton.disabled || !currentVariant)
        return;
      submitting = true;
      setCartBusy(true);
      updateVariant(false);
      errorBox.hidden = true;
      // Snapshot the selection. Never retry an uncertain add: it could duplicate a line.
      const body = new FormData(form);
      if (!body.get("selling_plan")) body.delete("selling_plan");
      body.set("sections", sectionIds().join(","));
      body.set("sections_url", `${root}cart`);
      let added = false;
      try {
        const result = await request(`${root}cart/add.js`, {
          method: "POST",
          body,
        });
        added = true;
        await refreshCart(result.sections);
        if (!recharge && $("[data-refill-popup]")) {
          prepareRefillPopup(
            currentVariant.options
              .filter((_, i) => i !== Number(product.dataset.colorIndex))
              .join(" · "),
          );
          openOverlay($("[data-refill-popup]"), addButton);
        } else openOverlay($("[data-cart-drawer]"), addButton);
      } catch (error) {
        try {
          await refreshCart();
        } catch (_) {
          /* Never retry an uncertain add. */
        }
        errorBox.textContent = added
          ? strings.refreshError
          : error.message || strings.error;
        errorBox.hidden = false;
      } finally {
        submitting = false;
        setCartBusy(false);
        updateVariant(false);
      }
    });
    updateVariant(false);
  }
  // Each add has its own immutable variant/plan snapshot; uncertain requests are never retried.
  const refillPopup = $("[data-refill-popup]");
  const quickForm = $("[data-quick-refill-form]", refillPopup || document);
  const quickVariants = JSON.parse(
    $("[data-quick-refill-data]")?.textContent || "[]",
  );
  let quickBusy = false;
  function quickSelection() {
    const variant = quickVariants.find(
      (v) => String(v.id) === $("input[name=id]:checked", quickForm)?.value,
    );
    const subscription =
      $("[name=refill_purchase]:checked", quickForm)?.value === "subscription";
    return { variant, subscription };
  }
  function syncQuickRefill() {
    if (!quickForm) return;
    const { variant, subscription } = quickSelection();
    $("[data-quick-once-price]").textContent = variant
      ? money(variant.price)
      : "—";
    $("[data-quick-plan-price]").textContent = variant?.plan_id
      ? money(variant.plan_price) + "/an"
      : "—";
    $("[value=subscription]", quickForm).disabled =
      quickBusy || !variant?.plan_id;
    $("[value=one-time]", quickForm).disabled =
      quickBusy || !!variant?.requires_plan;
    $$("[name=id]", quickForm).forEach((input) => {
      input.disabled = quickBusy;
    });
    const plan = $("[data-quick-plan-id]", quickForm);
    plan.value = subscription ? variant?.plan_id || "" : "";
    plan.disabled = !subscription || !variant?.plan_id;
    $("[data-quick-plan-terms]").hidden = !subscription || !variant?.plan_id;
    $("[data-quick-plan-unavailable]").hidden = !!variant?.plan_id;
    const add = $("[data-quick-add]", quickForm);
    add.disabled =
      quickBusy ||
      !variant?.available ||
      (subscription && !variant?.plan_id) ||
      (!subscription && variant?.requires_plan);
    add.textContent = quickBusy
      ? "Ajout en cours…"
      : !variant?.available
        ? strings.soldOut
        : subscription && !variant?.plan_id
          ? strings.subscriptionUnavailable
          : !subscription && variant?.requires_plan
            ? "Choisissez l’abonnement"
            : "Ajouter les recharges · " +
              money(subscription ? variant.plan_price : variant.price);
  }
  function prepareRefillPopup(hardness) {
    if (!quickForm || quickBusy) return;
    const variant =
      quickVariants.find((v) => v.title === hardness) ||
      quickVariants.find((v) => v.available) ||
      quickVariants[0];
    $$("[name=id]", quickForm).forEach((input) => {
      input.checked = String(variant?.id) === input.value;
    });
    $("[value=one-time]", quickForm).checked = true;
    $("[data-quick-error]", quickForm).hidden = true;
    syncQuickRefill();
  }
  quickForm?.addEventListener("change", () => {
    $("[data-quick-error]", quickForm).hidden = true;
    syncQuickRefill();
  });
  quickForm?.addEventListener("submit", async (event) => {
    event.preventDefault();
    if (quickBusy || cartBusy || $("[data-quick-add]", quickForm).disabled)
      return;
    const { variant, subscription } = quickSelection();
    const body = new FormData();
    body.set("id", variant.id);
    body.set("quantity", "1");
    if (subscription) body.set("selling_plan", variant.plan_id);
    body.set("sections", sectionIds().join(","));
    body.set("sections_url", `${root}cart`);
    quickBusy = true;
    setCartBusy(true);
    syncQuickRefill();
    let added = false;
    const errorBox = $("[data-quick-error]", quickForm);
    errorBox.hidden = true;
    try {
      const result = await request(`${root}cart/add.js`, {
        method: "POST",
        body,
      });
      added = true;
      await refreshCart(result.sections);
      if (activeOverlay === refillPopup) openOverlay($("[data-cart-drawer]"));
    } catch (error) {
      try {
        await refreshCart();
      } catch (_) {}
      errorBox.textContent = added
        ? strings.refreshError
        : error.message || strings.error;
      errorBox.hidden = false;
    } finally {
      quickBusy = false;
      setCartBusy(false);
      syncQuickRefill();
    }
  });
  syncQuickRefill();
  document.addEventListener("click", (event) => {
    const button = event.target.closest("[data-quantity-step]");
    if (!button) return;
    const input = $("input", button.closest("[data-quantity-stepper]"));
    const value = Number(input.value);
    input.value = Math.max(
      1,
      (Number.isInteger(value) ? value : 1) +
        Number(button.dataset.quantityStep),
    );
    input.dispatchEvent(new Event("input", { bubbles: true }));
  });
  document.addEventListener("input", (event) => {
    const stepper = event.target.closest("[data-quantity-stepper]");
    if (stepper)
      $("[data-quantity-step='-1']", stepper).disabled =
        Number(event.target.value) <= 1;
  });
  // No e-mail is stored or queried in the browser. Recognition comes from Shopify
  // or a successful newsletter response, never from typing/submitting an address.
  const clubPopup = $("[data-club-popup]");
  const clubSuccess = $('[data-club-result="success"]');
  const clubResult = $("[data-club-result]", clubPopup || document);
  let clubMember = Boolean($("[data-club-member]") || clubSuccess);
  const clubSessionKey = "toothy-club-session-v2";
  const clubSessionTimeout = 30 * 60 * 1000;
  let clubSession = { lastActivity: Date.now(), shown: false };
  try {
    if (clubMember) localStorage.setItem("toothy-club-member", "true");
    clubMember ||= localStorage.getItem("toothy-club-member") === "true";
    const saved = JSON.parse(sessionStorage.getItem(clubSessionKey) || "null");
    if (saved && Date.now() - saved.lastActivity < clubSessionTimeout)
      clubSession = saved;
  } catch (_) {}
  if (clubPopup) {
    let clubTimer;
    const saveClubSession = () => {
      try {
        sessionStorage.setItem(clubSessionKey, JSON.stringify(clubSession));
      } catch (_) {}
    };
    const showClub = () => {
      clubTimer = null;
      if (
        document.hidden ||
        activeOverlay ||
        cartBusy ||
        document.activeElement?.matches("input,textarea,select")
      )
        return;
      if (clubMember && !clubResult) return;
      if (clubSession.shown && !clubResult) return;
      clubSession = { lastActivity: Date.now(), shown: true };
      saveClubSession();
      openOverlay(clubPopup);
    };
    const scheduleClub = () => {
      if (
        !clubTimer &&
        (!clubMember || clubResult) &&
        (!clubSession.shown || clubResult)
      ) {
        clubTimer = setTimeout(showClub, clubResult ? 0 : 2200);
      }
    };
    const visitActivity = () => {
      if (document.hidden) return;
      const now = Date.now();
      if (now - clubSession.lastActivity >= clubSessionTimeout)
        clubSession.shown = false;
      if (now - clubSession.lastActivity > 10000 || !clubSession.shown) {
        clubSession.lastActivity = now;
        saveClubSession();
      }
      // A confirmed response is shown once, not reopened after its dismissal.
      if (!clubResult) scheduleClub();
    };
    scheduleClub();
    ["pointerdown", "keydown", "scroll"].forEach((type) =>
      document.addEventListener(type, visitActivity, { passive: true }),
    );
    document.addEventListener("visibilitychange", visitActivity);
    // An inscription completed in another tab immediately suppresses this invitation.
    window.addEventListener("storage", (event) => {
      if (event.key === "toothy-club-member" && event.newValue === "true") {
        clubMember = true;
        clearTimeout(clubTimer);
        if (activeOverlay === clubPopup && !clubResult) closeOverlay();
      }
    });
  }
  function initDetails(scope = document) {
    $$("details", scope).forEach((details) => {
      const summary = $("summary", details);
      if (!summary || details.dataset.ariaReady) return;
      details.dataset.ariaReady = "true";
      const sync = () =>
        summary.setAttribute("aria-expanded", String(details.open));
      sync();
      details.addEventListener("toggle", sync);
    });
  }
  initDetails();
  window.addEventListener("pageshow", (event) => {
    if (event.persisted && !cartBusy) {
      setCartBusy(true);
      refreshCart()
        .catch(() => cartError(strings.refreshError))
        .finally(() => setCartBusy(false));
    }
  });
  $$("[data-product-section]").forEach(initProduct);
  document.addEventListener("shopify:section:load", (event) => {
    initDetails(event.target);
    $$("[data-product-section]", event.target).forEach(initProduct);
  });
  document.addEventListener("shopify:section:unload", (event) => {
    if (activeOverlay && event.target.contains(activeOverlay)) closeOverlay();
  });
  document.addEventListener("click", (event) => {
    const tab = event.target.closest("[data-account-tab]");
    if (!tab) return;
    $$("[data-account-tab]").forEach((item) => {
      const selected = item === tab;
      item.setAttribute("aria-selected", String(selected));
      item.tabIndex = selected ? 0 : -1;
      document.getElementById(item.getAttribute("aria-controls")).hidden =
        !selected;
    });
  });
  document.addEventListener("keydown", (event) => {
    if (
      !event.target.matches("[data-account-tab]") ||
      !["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)
    )
      return;
    event.preventDefault();
    const tabs = $$("[data-account-tab]");
    const next =
      event.key === "Home"
        ? tabs[0]
        : event.key === "End"
          ? tabs.at(-1)
          : tabs[(tabs.indexOf(event.target) + 1) % tabs.length];
    next.click();
    next.focus();
  });
  matchMedia("(min-width: 901px)").addEventListener("change", (event) => {
    if (event.matches && activeOverlay?.matches("[data-mobile-menu]"))
      closeOverlay();
  });
})();
